import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Package,
  Printer,
  Sun,
  Palette,
  Layers,
  Layers3,
  Shirt,
  Scissors,
  Grid3x3,
  Flame,
  Sparkles,
  Droplets,
  Wrench,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { useLocalizedField } from "@/lib/i18n-db";

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
}

interface MobileMegaMenuProps {
  categories: Category[];
  type: "equipment" | "consumables";
  onLinkClick?: () => void;
}

const ICON_MAP: Record<string, React.ElementType> = {
  "impressora-dtf": Printer,
  "impressora-uv": Sun,
  "sublimacao": Palette,
  "impressora-ecosolvente": Layers,
  "textil-hibrida": Shirt,
  "laser-co2": Scissors,
  "mesa-corte-faca-cnc": Grid3x3,
  "prensas": Flame,
  "calandras": Flame,
  "laminadoras": Layers3,
  "bordados": Sparkles,
  "outros-equipamentos": Package,
  "tinta-dtg": Droplets,
  "tintas-sublimacao": Droplets,
  "tintas-dtf": Droplets,
  "tintas-uv": Droplets,
  "tintas-eco-solvente": Droplets,
  "peliculas-de-impressao": Layers,
  "po-transferencia": Sparkles,
  "pecas-acessorios": Wrench,
  "textil": Shirt,
};

const PRINTING_EQUIPMENT = new Set([
  "impressora-dtf",
  "impressora-uv",
  "sublimacao",
  "impressora-ecosolvente",
  "textil-hibrida",
]);

const INKS_CONSUMABLES = new Set([
  "tinta-dtg",
  "tintas-sublimacao",
  "tintas-dtf",
  "tintas-uv",
  "tintas-eco-solvente",
]);

const HIGHLIGHT_SLUGS = new Set(["impressora-dtf", "tintas-dtf"]);

const INTENT_ORDER: Record<string, number> = {
  "impressora-dtf": 0, "impressora-uv": 1, "sublimacao": 2, "impressora-ecosolvente": 3, "textil-hibrida": 4,
  "laser-co2": 0, "prensas": 1, "calandras": 2, "mesa-corte-faca-cnc": 3, "laminadoras": 4, "bordados": 5, "outros-equipamentos": 99,
  "tintas-dtf": 0, "tintas-sublimacao": 1, "tintas-uv": 2, "tintas-eco-solvente": 3, "tinta-dtg": 4,
  "peliculas-de-impressao": 0, "po-transferencia": 1, "textil": 2, "pecas-acessorios": 3,
};

export function MobileMegaMenu({ categories, type, onLinkClick }: MobileMegaMenuProps) {
  const { t } = useTranslation();
  const lf = useLocalizedField();
  const basePath = type === "equipment" ? "/equipamentos" : "/consumiveis";

  const { highlight, primary, secondary, primaryTitle, secondaryTitle, sectionLabel } = useMemo(() => {
    const sortFn = (a: Category, b: Category) =>
      (INTENT_ORDER[a.slug] ?? 50) - (INTENT_ORDER[b.slug] ?? 50);
    const primarySet = type === "equipment" ? PRINTING_EQUIPMENT : INKS_CONSUMABLES;
    const highlightSlug = type === "equipment" ? "impressora-dtf" : "tintas-dtf";
    const highlight = categories.find((c) => c.slug === highlightSlug) || null;
    const primary = categories.filter((c) => primarySet.has(c.slug) && c.slug !== highlightSlug).sort(sortFn);
    const secondary = categories.filter((c) => !primarySet.has(c.slug)).sort(sortFn);
    return {
      highlight,
      primary,
      secondary,
      primaryTitle: type === "equipment"
        ? t("megaMenu.groupPrinting", "Sistemas de Impressão")
        : t("megaMenu.groupInks", "Tintas Profissionais"),
      secondaryTitle: type === "equipment"
        ? t("megaMenu.groupFinishing", "Corte & Acabamento")
        : t("megaMenu.groupMaterials", "Materiais & Acessórios"),
      sectionLabel: type === "equipment" ? t("nav.equipment") : t("nav.consumables"),
    };
  }, [categories, type, t]);


  const isEquipment = type === "equipment";
  const themeClasses = isEquipment
    ? {
        topBar: "bg-equipment",
        headerIconBox: "bg-equipment text-equipment-foreground",
        pill: "bg-equipment/10 text-equipment border-equipment/20",
        itemIconBox: "bg-equipment/10 text-equipment",
        highlightWrap: "bg-equipment/5 border-equipment/30",
        highlightIcon: "bg-equipment text-equipment-foreground",
        highlightBadge: "bg-equipment text-equipment-foreground",
        viewAll: "text-equipment",
      }
    : {
        topBar: "bg-consumables",
        headerIconBox: "bg-consumables text-consumables-foreground",
        pill: "bg-consumables/10 text-consumables border-consumables/20",
        itemIconBox: "bg-consumables/10 text-consumables",
        highlightWrap: "bg-consumables/5 border-consumables/30",
        highlightIcon: "bg-consumables text-consumables-foreground",
        highlightBadge: "bg-consumables text-consumables-foreground",
        viewAll: "text-consumables",
      };
  const SectionIcon = isEquipment ? Printer : Droplets;

  const renderItemThemed = (category: Category, top = false) => {
    const Icon = ICON_MAP[category.slug] || Package;
    const name = lf(category as any, "name") || category.name;
    return (
      <Link
        key={category.id}
        to={`${basePath}/${category.slug}`}
        onClick={onLinkClick}
        className="flex items-center gap-3 py-2 px-2 rounded-md text-sm text-foreground hover:bg-accent active:bg-accent transition-colors"
      >
        <Icon className={cn("h-4 w-4 flex-shrink-0", themeClasses.viewAll)} strokeWidth={1.75} />
        <span className={cn("flex-1", top ? "font-semibold" : "font-medium")}>{name}</span>
        {top && (
          <Badge className={cn("h-4 px-1.5 text-[9px] font-bold uppercase border-0", themeClasses.highlightBadge)}>
            {t("megaMenu.topSeller", "Top Vendas")}
          </Badge>
        )}
      </Link>
    );
  };

  return (
    <section className="py-1">
      <div className="flex items-center justify-between px-2 pb-1">
        <div className="flex items-center gap-2">
          <SectionIcon className={cn("h-4 w-4", themeClasses.viewAll)} strokeWidth={2} />
          <h3 className="text-sm font-bold uppercase tracking-wide text-foreground">{sectionLabel}</h3>
        </div>
        <Link
          to={basePath}
          onClick={onLinkClick}
          className={cn("text-xs font-semibold flex items-center gap-1", themeClasses.viewAll)}
        >
          {t("megaMenu.viewAll", "Ver tudo")}
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div>
        {highlight && renderItemThemed(highlight, true)}
        {primary.map((c) => renderItemThemed(c))}
      </div>
      {secondary.length > 0 && (
        <>
           <h4 className="py-2 px-2 text-sm font-medium text-muted-foreground">
            {secondaryTitle}
           </h4>
           <div>{secondary.map((c) => renderItemThemed(c))}</div>
        </>
      )}
    </section>
  );
}

